import dayjs from 'dayjs';
import cookie from 'js-cookie';
import type { PdpContext } from 'types';
import type { CapiProductPage } from 'types/capi/pdp';
import type { User } from 'types/User';
import * as uuid from 'uuid';

import braze from '~/services/braze.client';
import facebook from '~/services/facebook';
import facebookPixel from '~/services/facebook.pixel';
import gtm from '~/services/gtm.client';
import tiktok from '~/services/tiktok.pixel';
import twitter from '~/services/twitter.pixel';
import {
  getEntrypoint,
  getPreviousContext,
  getPreviousPagePath,
  getPreviousPageType,
  getSourceAndMedium
} from '~/utils/navigationContext';

import UserEvent from './UserEvent';

type EventData = {
  clientIp: string | null;
  isBot: boolean;
  product: CapiProductPage;
  user: User;
};

export default class PdpViewEvent extends UserEvent<EventData> {
  private readonly eventId = uuid.v4();
  private readonly pdpContext = getPreviousContext<PdpContext>();

  private getProductId() {
    let productId = `${this.data.product.product_id}`;

    if (this.data.product.sub_type === 'clothing') {
      productId += `-${this.data.product.variant_id}`;
    }

    return productId;
  }

  private getPrice() {
    return (
      this.data.product.price.sale ??
      this.data.product.price.regular ??
      undefined
    );
  }

  private getCurrency() {
    return this.data.product.price.currency;
  }

  private getNumOfListings() {
    return this.data.product.stock_listings.affiliates.length;
  }

  async sendToFacebook() {
    const eventId = this.eventId;

    await facebookPixel.fireEvent({
      data: {
        content_ids: [this.getProductId()],
        content_type: 'product_group',
        currency: this.getCurrency(),
        value: this.getPrice()
      },
      eventID: eventId,
      eventName: facebookPixel.StandardEventNames.ViewContent
    });

    const hashedProfile = await facebook.getHashedProfile(
      this.data.user.profile
    );

    const serverEventData = [
      {
        action_source: 'website',
        custom_data: {
          brand: this.data.product.brand ?? '',
          content_ids: [this.data.product.product_id],
          content_name: this.data.product.name ?? '',
          content_type: 'product_group',
          currency: this.data.product.price?.currency ?? '',
          value:
            this.data.product.price?.sale ??
            this.data.product.price.regular ??
            0
        },
        event_id: eventId,
        event_name: facebookPixel.StandardEventNames.ViewContent,
        event_source_url: this.data.product.canonical_url,
        event_time: dayjs().unix(),
        user_data: {
          client_ip_address: this.data.clientIp,
          client_user_agent: navigator?.userAgent,
          external_id: this.data.user.id ?? 'guest',
          fbc: cookie.get('_fbc'),
          fbp: cookie.get('_fbp'),
          ...hashedProfile
        }
      }
    ];

    this.serverEvents.push({ data: serverEventData, type: 'facebook' });
  }

  sendToTikTok() {
    return tiktok.fireEvent('ViewContent', {
      content_ids: [this.getProductId()],
      content_type: 'product',
      currency: this.getCurrency(),
      value: this.getPrice()
    });
  }

  collectServerEvents() {
    const serverEventData = {
      currency: this.getCurrency(),
      entry_point: getEntrypoint(),
      event_name: 'pdp_view' as const,
      is_logged_in: this.data.user.isLoggedIn,
      item: this.getItem(),
      list_id: this.pdpContext?.listId,
      num_of_listings: this.getNumOfListings(),
      page_path: window.location.pathname,
      page_type: 'pdp' as const,
      platform: 'web' as const,
      position: this.pdpContext?.position,
      previous_page_path: getPreviousPagePath(),
      previous_page_type: getPreviousPageType(),
      product_brand: this.data.product.brand,
      product_id: this.data.product.product_id,
      product_view_price: this.getPrice(),
      source_medium: getSourceAndMedium(),
      stock_status: this.data.product.stock_status,
      style_code: this.data.product.style_code,
      user_id: this.data.user.id,
      value: this.getPrice()
    };

    this.serverEvents.push({ data: serverEventData, type: 'pdp_view' });
  }

  private getItem() {
    return {
      index: this.pdpContext?.position,
      item_brand: this.data.product.brand,
      item_category: this.data.product.genders?.join(', '),
      item_category2: this.data.product.sub_type,
      item_id: this.data.product.style_code,
      item_name: this.data.product.name,
      item_variant: this.data.product.model,
      location_id: this.data.product.stock_status,
      price:
        this.data.product.price.sale ??
        this.data.product.price.regular ??
        undefined
    };
  }

  sendToTwitter() {
    return twitter.fireEvent(twitter.EventIds.ViewContent, {
      contents: [{ content_id: this.getProductId(), content_type: 'product' }],
      currency: this.getCurrency(),
      value: this.getPrice()
    });
  }

  sendToGtm() {
    return gtm.Ecommerce.viewListItem(
      this.data.product,
      this.pdpContext?.position
    );
  }

  sendToBraze(): void {
    return braze.logPdpViewEvent(this.data.product._event_data.marketing);
  }
}
